import React, { useContext, useCallback } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Switch, makeStyles, Label, Field, Radio, RadioGroup } from '@fluentui/react-components';
import { ValidatedCollapseCard } from '@bingads-webui-campaign-react/validated-form-cards';
import { FormCardBody, FormCardHeader, AccordionButton, FormCardError, Spacer } from '@bingads-webui-fluent-component/form-cards';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withValidation, AdapterFactory } from '@bingads-webui-react/validation';

const useClasses = makeStyles({
  root: {
    marginBottom: '8px',
  },
  switch: {
    alignItems: 'center',
    '& input[type="checkbox"]': {
      height: '100%',
      width: '100%',
    },
  },
});

const CustomAdapter = AdapterFactory({
  valueToInject: 'checked',
  convertEventToValue: event => event.target.checked,
});

const CustomRadioGroupAdapter = AdapterFactory({
  valueToInject: 'value',
  convertEventToValue: event => event.target.value,
});

const SwitchInternal = ({
  checked,
  onChange,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useClasses();

  const onChangeInternal = useCallback((e) => {
    e.stopPropagation();
    onChange(e);
  }, [onChange]);

  return (
    <Switch
      checked={checked}
      onChange={onChangeInternal}
      label={<Label size="medium"> {i18n.getString(_TL_('Enable brand showroom'))} </Label>}
      className={classes.switch}
    />
  );
};

SwitchInternal.propTypes = {
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

const ValidatedSwitch = withValidation(SwitchInternal, CustomAdapter);


const RadioGroupInternal = ({
  value,
  onChange,
  children,
  disabled,
}) => (
  <RadioGroup
    value={value}
    onChange={onChange}
    disabled={disabled}
  >
    {children}
  </RadioGroup>
);

RadioGroupInternal.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  disabled: PropTypes.bool.isRequired,
};

const ValidatedRadioGroup = withValidation(RadioGroupInternal, CustomRadioGroupAdapter);

export const BrandShowroomPanel = ({
  switchValidationProps,
  radioValidationProps,
  isEnabled,
  ...cardProps
}) => {
  const classes = useClasses();

  const i18n = useContext(I18nContext);

  return (
    <ValidatedCollapseCard
      className={classNames('brand-showroom-panel', classes.root)}
      {...cardProps}
    >
      <FormCardHeader>
        <div className="brand-showroom-switch">
          <ValidatedSwitch validationProps={switchValidationProps} />
        </div>
        <Spacer />
        <FormCardError />
        <AccordionButton />
      </FormCardHeader>
      <FormCardBody>
        <Field label={<Label size="medium"> {i18n.getString(_TL_('Where do you want your ads to serve?'))} </Label>} >
          <div className="brand-showroom-radio-group">
            <ValidatedRadioGroup
              validationProps={radioValidationProps}
              disabled={!isEnabled}
            >
              <Radio
                value="All"
                label={i18n.getString(_TL_('Existing Performance max supply and Brand showroom'))}
                className="brand-showroom-radio-group__All"
              />
              <Radio
                value="BSROnly"
                label={i18n.getString(_TL_('Only enable campaign for Brand showroom'))}
                className="brand-showroom-radio-group__BSROnly"
              />
            </ValidatedRadioGroup>
          </div>
        </Field>
      </FormCardBody>
    </ValidatedCollapseCard>
  );
};

BrandShowroomPanel.propTypes = {
  switchValidationProps: PropTypes.shape({
    data: PropTypes.bool,
  }).isRequired,
  radioValidationProps: PropTypes.shape({}).isRequired,
  isEnabled: PropTypes.bool.isRequired,
};
